/**
 * Reads a feature flag from a route's `beforeLoad`, through the query client
 * under the key `useFeatureFlags` uses, so a page that gates on the flag and a
 * component that reads it share one evaluation.
 *
 * Fails closed: when the evaluation fails, the flag reads as off, so a gated
 * page redirects as it does when the flag is off instead of showing the error
 * page.
 *
 * Aliases: route flag guard, flag in beforeLoad, feature flag redirect
 */

import type { QueryClient } from "@tanstack/react-query";

import { evaluateFeatureFlagsForUser } from "~/lib/functions/feature-flags";
import type { FeatureFlagKey } from "~/lib/shared/feature-flags";

export async function isFeatureFlagOn(
  queryClient: QueryClient,
  key: FeatureFlagKey,
): Promise<boolean> {
  try {
    const flags = await queryClient.ensureQueryData({
      queryKey: ["featureFlags", [key]],
      queryFn: () => evaluateFeatureFlagsForUser({ data: { keys: [key] } }),
      staleTime: 60 * 1000,
    });
    return flags[key] === true;
  } catch {
    return false;
  }
}
